import React, { useContext, useEffect, useMemo } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, Grid, ListItemSecondaryAction, ListSubheader, Stack } from "@material-ui/core";
import { useFormik } from "formik";
import { validations } from "./DialogNodeValidations";
import { libraries } from "./DialogNodeLibraries";
import { extras } from "./DialogNodeExtras";
import * as forms from "./forms";
import { Delete } from "@material-ui/icons";
import Swal from 'sweetalert2';
import { usePropsClipboard } from "..";
import { createContext } from "react";
import { useState } from "react";

export const ItemTypes = {
  LIBRARY_ITEM: 'library_item'
}

export const modesType = {
  DIALOG: 0,
  DRAWER: 1,
}

const DialogNodeContext = createContext({
  clipboard: 
    {
      setCopy: (value = false, closeAfterCopy = true) => {}, 
      setPaste: (value = false) => {}, 
      setCopyProps: (value = {prop1: (prop) => prop, prop2: (prop) => prop}) => {}, 
      setPasteProps: (value = []) => {}
    }
  });

export const useDialogNodeContext = () => useContext(DialogNodeContext);

export const DialogNode = ({ caption, data = {}, formData, type, open, onClose, onSubmit, maxWidth="sm", mode = modesType.DIALOG, onDeleteClick, HeaderAction, DialogAction = DialogActionsDefault, showConfirm }) => {

  const propsClipboard = usePropsClipboard();
  const [cbState, setCbState] = useState({copy: false, paste: false, closeAfterCopy: true, copyProps: [], pasteProps: []});
  const [show, setShow] = useState(true);

  const setCopy = (copy, closeAfterCopy = true) => {
    setCbState(state => ({...state, copy, closeAfterCopy}));
  }
  
  const setPaste = (paste) => {
    setCbState(state => ({...state, paste}));
  }

  const setCopyProps = (copyProps) => {
    setCbState(state => ({...state, copy: true, copyProps}));
  }
  
  const setPasteProps = (pasteProps) => {
    setCbState(state => ({...state, paste: true, pasteProps}));
  }

  const fmk = useFormik({
    initialValues: {},
    validationSchema: () => validations[type] ? validations[type]() : validations.default(),
    onSubmit: values => {
      onSubmit && onSubmit(values, data);
    },
  })

  useEffect(()=>{
    if(open){
      fmk.setValues(data)
    }
  }, [data, open])

  const handleClose = () => {
    if (fmk.values === data) {
      onClose();
      return;
    }
    Swal
      .fire({
        icon: 'question',
        title: 'Cerrar?',
        html: `Está a punto de cerrar este diálogo sin guardar cambios, precione cerrar para confirmar y salir sin guardar`,
        showCancelButton: true,
        confirmButtonText: 'Cerrar',
        cancelButtonText: 'Continuar editando',
      })
      .then((result) => {
        if (result.isConfirmed) {
          onClose();
        }
      })
  };

  const handleCopy = () => {
    propsClipboard.copyPropsFromFmk({fmk, copyProps: cbState.copyProps});
    cbState.closeAfterCopy && handleClose();
  }
  
  const handlePaste = () => {
    setShow(false);
    propsClipboard.pastePropsIntoFmk({fmk, pasteProps: cbState.pasteProps})
      .catch(err => {
        setShow(true);
        console.log(err);
      })
      .finally(()=>{
        setShow(true);
      });
  }
  
  const Form = useMemo(() => {
    setShow(true);
    setCbState({copy: false, paste: false, closeAfterCopy: true, copyProps: [], pasteProps: []})
    return forms[type];
  }, [type]);
  const Extra =  useMemo(() => extras[type], [type]);
  const Libraries =  useMemo(() => libraries[type], [type]);

  return (
    <DialogNodeContext.Provider value={{clipboard: {setCopy, setPaste, setCopyProps, setPasteProps}}}>
      {mode === modesType.DRAWER &&
        <Drawer
          anchor={"right"}
          open={open}
          onClose={handleClose}
          keepMounted={true}
        >
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={0.5}
            padding={1}
          >
            {Libraries && 
              <Stack
                direction="column"
                justifyContent="flex-start"
                alignItems="stretch"
                spacing={0.5}
                padding={1}
              >
                <Libraries fmk={fmk} formData={formData}/>
              </Stack>
            }
            <Stack
              direction="column"
              justifyContent="flex-start"
              alignItems="stretch"
              spacing={0.5}
              padding={1}
              width= "600px"
            >
              <ListSubheader component="div" id="nested-list-subheader">
                {caption}
                <ListItemSecondaryAction>
                  {onDeleteClick && 
                    <Button onClick={(event)=>onDeleteClick(data).then(()=>onClose())} color="primary">
                      <Delete /> Borrar
                    </Button>
                  }
                  {!!cbState.copy && 
                    <Button onClick={handleCopy} color="primary">
                      Copiar
                    </Button>
                  }
                  {!!cbState.paste &&
                    <Button onClick={handlePaste} color="primary">
                      Pegar
                    </Button>
                  }
                </ListItemSecondaryAction>
                {HeaderAction && 
                  <ListItemSecondaryAction>
                    <HeaderAction fmk={fmk}/>
                  </ListItemSecondaryAction>                
                }
              </ListSubheader>
              <Grid container style={{width: "100%"}}>
                {show && Form && <Form fmk={fmk} formData={formData}/>}
              </Grid>
              <Stack               
                direction="row"
                justifyContent="flex-start"
                alignSelf="flex-end"
                alignItems="stretch"
                spacing={0.5}
              >
                <DialogAction onCancel={handleClose} onConfirm={fmk.submitForm} disabled={!fmk.isValid || !fmk.touched} showConfirm={showConfirm}/>
              </Stack>
              {Extra && 
                <Grid container style={{width: "100%"}}>
                  <Extra fmk={fmk} formData={formData}/>
                </Grid>
              }      
            </Stack>
          </Stack>
        </Drawer>
      }
      {mode === modesType.DIALOG &&
        <Dialog fullWidth onClose={handleClose} open={open} maxWidth={maxWidth} keepMounted={true}>
          <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
            <Box display="flex" alignItems="center">
              <Box flexGrow={1} >{caption}</Box>
              {HeaderAction && 
                <Box>
                  <HeaderAction fmk={fmk}/>
                </Box>                
              }              
            </Box>
          </DialogTitle>
          <DialogContent>
            <Grid item xs={12}>
              <Grid container>
                {Form && <Form fmk={fmk} formData={formData}/>}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions style={{paddingTop: 4}}>
            <DialogAction onCancel={handleClose} onConfirm={fmk.submitForm} disabled={!fmk.isValid || !fmk.touched} showConfirm={showConfirm}/>
          </DialogActions>
        </Dialog> 
      }
    </DialogNodeContext.Provider>
  )
}

const DialogActionsDefault = ({onCancel, onConfirm, disabled, showConfirm = 1}) => {
  return (
    <>
      <Button autoFocus onClick={onCancel} color="primary" variant="outlined">
        Cancelar
      </Button>
      {showConfirm === 1 && 
        <Button autoFocus disabled={disabled} onClick={onConfirm}  color="primary" variant="contained">
          Confirmar
        </Button>
      }
    </>
  )
}